import { getAuthHeader, getUserInfo } from '../utils/tokenManager';
import { API_URL } from '../config/apiConfig';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_SALES_CONDITION_API_BASE_URL;
  if (envBase) return envBase;

  if (typeof window !== 'undefined') {
    const host = String(window.location?.hostname || '').toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:8081/api/v1';
    }
  }

  return 'https://inapi.tripbox.kr/api/v1';
};

const API_BASE_URL = resolveApiBaseUrl();

const toText = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  if (!s || s.toLowerCase() === 'null') return '';
  return s;
};

const fetchTableFirstRow = async ({ procedureName, payload }) => {
  try {
    const url = `${API_URL}?procedureName=${procedureName}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) return null;
    const data = await response.json().catch(() => null);
    const table = data && (data.table || data.Table);
    return Array.isArray(table) && table.length > 0 ? table[0] : null;
  } catch (_) {
    return null;
  }
};

const fetchUpdatedByInfo = async ({ companyNo, userNo }) => {
  const safeCompanyNo = companyNo != null ? String(companyNo) : '';
  const safeUserNo = Number(userNo);

  const profileRow = await fetchTableFirstRow({
    procedureName: 'DBO.CW_S_%EC%9C%A0%EC%A0%80%EC%A0%95%EB%B3%B4',
    payload: {
      Table1: [
        { 컬럼명: '@업체번호', 값: safeCompanyNo, 리스트값: null },
        { 컬럼명: '@유저번호', 값: safeUserNo, 리스트값: null },
      ],
    },
  });

  const staffRow = await fetchTableFirstRow({
    procedureName: 'DBO.CW_S_%EC%82%AC%EC%9B%90%EC%A0%95%EB%B3%B4',
    payload: {
      Table1: [
        { 컬럼명: '@업체번호', 값: safeCompanyNo },
        { 컬럼명: '@유저번호', 값: safeUserNo },
      ],
    },
  });

  return {
    updatedCompanyName: toText(profileRow?.업체명),
    updatedCompanyLogo: toText(profileRow?.회사로고),
    updatedUserName: toText(profileRow?.유저명) || toText(staffRow?.한글이름) || toText(staffRow?.유저명),
    updatedUserTitle: toText(staffRow?.직책명),
    updatedUserEmail: toText(staffRow?.이메일) || toText(staffRow?.대표이메일) || toText(staffRow?.아이디),
    updatedUserPhone: toText(staffRow?.휴대폰) || toText(staffRow?.대표전화),
    updatedUserProfile: toText(staffRow?.프로필),
  };
};

export const getSalesCondition = async ({ companyNo, productNo }) => {
  if (!companyNo) {
    const u = getUserInfo() || {};
    companyNo = u.companyNo;
  }

  const response = await fetch(
    `${API_BASE_URL}/sales-conditions/${encodeURIComponent(String(productNo))}?companyNo=${encodeURIComponent(String(companyNo))}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }
  );

  if (!response.ok) {
    throw new Error(`판매조건 조회 실패: ${response.status}`);
  }

  const data = await response.json();
  return {
    판매가능시작일: toText(data?.sellStartDate),
    판매가능종료일: toText(data?.sellEndDate),
    인플루언서모집시작일: toText(data?.recruitStartDate),
    인플루언서모집종료일: toText(data?.recruitEndDate),
    필수작업물제출기한: toText(data?.deliverableSubmitDeadline),
    협의가능: !!data?.negotiable,
    추천타겟고객층: toText(data?.targetCustomer),
    판매수수료방식: toText(data?.commissionPolicy),
    비고사항: toText(data?.note),
    콘텐츠제작지원: toText(data?.contentCreationSupport),
    필수작업물템플릿: data?.deliverableTemplate && typeof data.deliverableTemplate === 'object' ? data.deliverableTemplate : {},
    지정거래처목록: Array.isArray(data?.invitedCompanyNos) ? data.invitedCompanyNos : [],
  };
};

export const saveSalesCondition = async ({
  companyNo,
  userNo,
  productNo,
  invitedRequestNote,
  salesCondition
}) => {
  if (!companyNo || !userNo) {
    const u = getUserInfo() || {};
    companyNo = companyNo ?? u.companyNo;
    userNo = userNo ?? u.userNo;
  }

  const updatedBy = await fetchUpdatedByInfo({ companyNo, userNo });

  const invitedCompaniesRaw = salesCondition?.지정거래처상세목록;
  const invitedCompanies = Array.isArray(invitedCompaniesRaw) ? invitedCompaniesRaw : [];
  const invitedCompanyNosRaw = salesCondition?.지정거래처목록;
  const invitedCompanyNos = Array.isArray(invitedCompanyNosRaw)
    ? invitedCompanyNosRaw
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n) && n > 0)
    : [];

  const response = await fetch(`${API_BASE_URL}/sales-conditions/${encodeURIComponent(String(productNo))}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      companyNo: Number(companyNo),
      userNo: Number(userNo),
      productName: toText(salesCondition?.상품명),
      invitedRequestNote: toText(invitedRequestNote),
      sellStartDate: toText(salesCondition?.판매가능시작일),
      sellEndDate: toText(salesCondition?.판매가능종료일),
      recruitStartDate: toText(salesCondition?.인플루언서모집시작일),
      recruitEndDate: toText(salesCondition?.인플루언서모집종료일),
      deliverableSubmitDeadline: toText(salesCondition?.필수작업물제출기한),
      negotiable: !!salesCondition?.협의가능,
      targetCustomer: toText(salesCondition?.추천타겟고객층),
      commissionPolicy: toText(salesCondition?.판매수수료방식),
      note: toText(salesCondition?.비고사항),
      contentCreationSupport: toText(salesCondition?.콘텐츠제작지원),
      deliverableTemplate:
        salesCondition?.필수작업물템플릿 && typeof salesCondition.필수작업물템플릿 === 'object' ? salesCondition.필수작업물템플릿 : {},
      invitedCompanyNos,
      invitedCompanies,
      updatedCompanyName: toText(updatedBy?.updatedCompanyName),
      updatedCompanyLogo: toText(updatedBy?.updatedCompanyLogo),
      updatedUserName: toText(updatedBy?.updatedUserName),
      updatedUserTitle: toText(updatedBy?.updatedUserTitle),
      updatedUserEmail: toText(updatedBy?.updatedUserEmail),
      updatedUserPhone: toText(updatedBy?.updatedUserPhone),
      updatedUserProfile: toText(updatedBy?.updatedUserProfile)
    })
  });

  if (!response.ok) {
    throw new Error(`판매조건 저장 실패: ${response.status}`);
  }

  return response.json();
};
